import type { AuthSession } from "../auth/models/session.model";
import type { SuiteModuleEntry } from "./registry";

export const SUITE_LAST_MODULE_KEY = "itaas.suite.lastModule";
export const SUITE_RETURN_TO_KEY = "itaas.suite.returnTo";
export const SUITE_RETURN_TO_PATH_KEY = "itaas.suite.returnToPath";

export function saveLastModule(moduleId: string): void {
  localStorage.setItem(SUITE_LAST_MODULE_KEY, moduleId);
}

export function loadLastModule(): string | null {
  return localStorage.getItem(SUITE_LAST_MODULE_KEY);
}

function resolveSuiteNavigationStorage(): Storage {
  if (typeof window === "undefined") return sessionStorage;
  if (import.meta.env.VITE_AUTH_STORAGE === "session") return sessionStorage;
  return localStorage;
}

const navigationStorage = resolveSuiteNavigationStorage();

export function clearReturnTo(): void {
  navigationStorage.removeItem(SUITE_RETURN_TO_KEY);
  navigationStorage.removeItem(SUITE_RETURN_TO_PATH_KEY);
  sessionStorage.removeItem(SUITE_RETURN_TO_KEY);
  sessionStorage.removeItem(SUITE_RETURN_TO_PATH_KEY);
}

export function loadReturnToModuleId(): string | null {
  return (
    navigationStorage.getItem(SUITE_RETURN_TO_KEY) ??
    sessionStorage.getItem(SUITE_RETURN_TO_KEY)
  );
}

export function saveReturnToModuleId(moduleId: string): void {
  navigationStorage.setItem(SUITE_RETURN_TO_KEY, moduleId);
}

export function loadReturnToPath(): string | null {
  const path =
    navigationStorage.getItem(SUITE_RETURN_TO_PATH_KEY) ??
    sessionStorage.getItem(SUITE_RETURN_TO_PATH_KEY);
  if (!path?.startsWith("/")) return null;
  return sanitizeModuleReturnPath(path);
}

/** Chemins invalides pour un handoff module (évite boucles /login ↔ relay). */
export function sanitizeModuleReturnPath(path: string | null | undefined): string | null {
  if (!path?.startsWith("/")) return null;
  const blocked = ["/login", "/auth/handoff", "/auth/logout", "/auth/logout-sync", "/auth/relay"];
  if (blocked.some((entry) => path === entry || path.startsWith(`${entry}?`))) {
    return null;
  }
  return path;
}

export function saveReturnToPath(path: string): void {
  const safe = sanitizeModuleReturnPath(path);
  if (!safe) return;
  navigationStorage.setItem(SUITE_RETURN_TO_PATH_KEY, safe);
}

/** Encode la session pour handoff cross-app (fragment — non envoyé au serveur). */
export function buildModuleHandoffUrl(
  baseUrl: string,
  handoffPath: string,
  session: AuthSession,
  targetPath = "/",
): string {
  const safeTarget = sanitizeModuleReturnPath(targetPath) ?? "/";
  const payload = btoa(
    encodeURIComponent(
      JSON.stringify({
        session,
        targetPath: safeTarget,
        issuedAt: Date.now(),
      }),
    ),
  );
  const origin = (baseUrl ?? "").replace(/\/$/, "");
  if (!origin) {
    throw new Error("URL du module manquante pour le handoff.");
  }
  return `${origin}${handoffPath}#payload=${encodeURIComponent(payload)}`;
}

/**
 * Après connexion Suite : portail modules par défaut.
 * Handoff direct uniquement si l'utilisateur revient d'un produit (?module=inventory).
 */
export function pickPostLoginModule(
  accessible: SuiteModuleEntry[],
  options: {
    returnToModuleId?: string | null;
    lastModuleId?: string | null;
  },
): SuiteModuleEntry | "launcher" | "denied" {
  if (!accessible.length) return "denied";

  const { returnToModuleId } = options;

  if (returnToModuleId) {
    const direct = accessible.find((m) => m.id === returnToModuleId);
    if (direct) return direct;
  }

  return "launcher";
}
